import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  FormRow,
  RightDrawer,
} from '@projexn/ui';
import { type QuoteStatus } from '@projexn/shared';
import { api } from '../lib/api';

interface Quote {
  id: string;
  tenderId: string;
  version: number;
  status: QuoteStatus;
  totalCents: number;
  validUntil: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface Tender {
  id: string;
  name: string;
  metadata?: Record<string, unknown>;
}

function refOf(t: Tender): string {
  const r = t.metadata?.reference;
  return typeof r === 'string' ? r : t.id.slice(-6).toUpperCase();
}

const STATUS_TONE: Record<QuoteStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'neutral',
  sent: 'info',
  accepted: 'success',
  declined: 'danger',
  expired: 'warning',
  revised: 'warning',
};

export function QuotesPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const quotesQ = useQuery({
    queryKey: ['quotes'],
    queryFn: () => api.get<{ items: Quote[] }>('/api/quotes'),
  });
  const tendersQ = useQuery({
    queryKey: ['tenders'],
    queryFn: () => api.get<{ items: Tender[] }>('/api/tenders'),
  });

  const send = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/quotes/${id}/send`, {
        recipientEmail: window.prompt('Send quote to which email?') ?? '',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Quotes</h1>
          <p className="text-sm text-zinc-500">Generated against tenders.</p>
        </div>
        <Button onClick={() => setCreating(true)}>New quote</Button>
      </header>

      <Card className="overflow-hidden">
        <DataTable
          rows={quotesQ.data?.items ?? []}
          rowKey={(q) => q.id}
          columns={[
            { key: 'ref', header: 'Quote', render: (q) => <code className="text-xs">{q.id.slice(-8)} v{q.version}</code> },
            { key: 'status', header: 'Status', render: (q) => <Badge tone={STATUS_TONE[q.status]}>{q.status}</Badge> },
            { key: 'total', header: 'Total', render: (q) => `$${(q.totalCents / 100).toLocaleString()}` },
            { key: 'sent', header: 'Sent', render: (q) => q.sentAt ? new Date(q.sentAt).toLocaleDateString() : '—' },
            {
              key: 'actions',
              header: '',
              render: (q) => q.status === 'draft' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    send.mutate(q.id);
                  }}
                >
                  Send
                </Button>
              ) : null,
            },
          ]}
          empty={<p className="px-6 py-12 text-center text-sm text-zinc-500">No quotes yet.</p>}
        />
      </Card>

      <RightDrawer open={creating} onClose={() => setCreating(false)} title="New quote">
        <NewQuoteForm
          tenders={tendersQ.data?.items ?? []}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['quotes'] });
          }}
        />
      </RightDrawer>
    </div>
  );
}

function NewQuoteForm({ tenders, onCreated }: { tenders: Tender[]; onCreated: () => void }) {
  const [tenderId, setTenderId] = useState<string>('');

  const mutate = useMutation({
    mutationFn: () =>
      api.post('/api/quotes', {
        tenderId,
        items: [],
      }),
    onSuccess: onCreated,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutate.mutate();
      }}
      className="space-y-4"
    >
      <FormRow>
        <Field label="Tender" required>
          <select
            required
            value={tenderId}
            onChange={(e) => setTenderId(e.target.value)}
            className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm"
          >
            <option value="">— select —</option>
            {tenders.map((t) => (
              <option key={t.id} value={t.id}>
                {refOf(t)} · {t.name}
              </option>
            ))}
          </select>
        </Field>
      </FormRow>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>Create draft</Button>
      </div>
    </form>
  );
}

